import { 
  ExpressionKinds,
  IExpressionBehavior,
  ICalculationExpressionBehavior,
  IConditionalExpressionBehavior,
  IDateDurationExpressionBehavior,
  ITimeSpanExpressionBehavior,
  IDateComparisonExpressionBehavior,
  IUndefinedExpressionBehavior,
  ExpressionReferenceKinds,
  ExpressionOperatorKinds,
  ExpressionLogicalOperatorKinds,
  ExpressionFunctionKinds,
  IExpressionContentBrackets,
  ICalculationExpressionObjectReference,
  IConditionalExpressionObjectReference,
  IConditionalExpressionPresentation,
  DateDurationKinds,
  TimeSpanKinds,
  IExpressionBehaviorSpecifiable,
  ILinkedObjectSpecifiable,
} from "../../../../../../../documaker/domain/format/objects";
import { ICalculateItemBean } from "../../../beans/objects/items";
import { ItemsTranslator } from "./ItemsTranslator";
import { CalculationConditionalExpressionBehaviorTranslator } from "./CalculationConditionalExpressionBehaviorTranslator";
import { IntegerTranslator, MapTranslator, ArrayComplexTranslator, FloatTranslator } from "../../values";
import { IXElementContentsObjectRelationalBean } from "../../../beans";
import { ICalculateObjectBean } from "../../../beans/objects";

export class ExpressionBehaviorTranslator {
  from(bean: ICalculateObjectBean): IExpressionBehavior {
    return this.toTranslator(this.integer.from(bean["@form-type"])).from(bean);
  }

  to(object: IExpressionBehaviorSpecifiable) {
    return this.toTranslator(object.expressionKind).to(object.expressionBehavior);
  }

  toTranslator(kind: ExpressionKinds): IExpressinBehaviorTranlatable {
    switch (kind) {
      case ExpressionKinds.Calculation:
        return this.calculaion;
      case ExpressionKinds.Conditional:
        return this.conditional;
      case ExpressionKinds.DateDuration:
        return this.dateDuration;
      case ExpressionKinds.TimeSpan:
        return this.timeSpan;
      case ExpressionKinds.DateComparison:
        return this.dateComparison;
      default:
        return this.undefined;
    }
  }

  private readonly integer = new IntegerTranslator();
  private readonly calculaion = new CalculationExpressionBehaviorTranslator();
  private readonly conditional = new ConditionalExpressionBehaviorTranslator();
  private readonly dateDuration = new DateDurationExpressionBehaviorTranslator();
  private readonly timeSpan = new TimeSpanExpressionBehaviorTranslator();
  private readonly dateComparison = new DateComparisonExpressionBehaviorTranslator();
  private readonly undefined = new UndefinedExpressionBehaviorTranslator();
}

export interface IExpressinBehaviorTranlatable {
  from(bean: ICalculateObjectBean): IExpressionBehavior;
  to(object: IExpressionBehavior): { calculateitem: ICalculateItemBean | ICalculateItemBean[], relation: IXElementContentsObjectRelationalBean };
}

export class CalculationExpressionBehaviorTranslator implements IExpressinBehaviorTranlatable {
  from(bean: ICalculateObjectBean): ICalculationExpressionBehavior {
    return {
      contents: this.items.from(
        bean.calculateitem,
        s => ({
          kind: this.integer.from(s["@item-type"]),
          reference: this.reference.from(s),
          operator: this.integer.from(s["@operator"]),
          brackets: this.brackets.from(s),
        })),
      conditionalExpressionBehavior: this.conditionalExpressionBehavior.from(bean),
    };
  }

  to(object: ICalculationExpressionBehavior): { calculateitem: ICalculateItemBean | ICalculateItemBean[], relation: IXElementContentsObjectRelationalBean } {
    return {
      calculateitem: this.items.to(
        object.contents,
        s => {
          const result = this.reference.to(s.reference, s.kind, s.operator);
          result["@operator"] = this.integer.to(s.operator);
          const brackets = this.brackets.to(s.brackets);
          result["@priority-front"] = brackets.priorityFront;
          result["@priority-back"] = brackets.priorityBack;
          return result;
        },
      ),
      relation: this.conditionalExpressionBehavior.to(object.conditionalExpressionBehavior),
    };
  }

  private readonly reference = {
    from: (bean: ICalculateItemBean): ICalculationExpressionObjectReference | number | ExpressionFunctionKinds => {
      switch (this.integer.from(bean["@item-type"])) {
        case ExpressionReferenceKinds.Object:
          return this.expressionObjectReference.from(bean);
        case ExpressionReferenceKinds.Fixed:
          return this.float.from(bean["@value"]);
          case ExpressionReferenceKinds.Function:
            return this.functionKind.from(bean["@value"]);
          default:
          return undefined;
      }
    },
    to: (object: ICalculationExpressionObjectReference | number | ExpressionFunctionKinds, kind: ExpressionReferenceKinds, operator: ExpressionOperatorKinds): ICalculateItemBean => {
      switch (kind) {
        case ExpressionReferenceKinds.Object:
          return this.expressionObjectReference.to(object as ICalculationExpressionObjectReference, operator);
        case ExpressionReferenceKinds.Fixed:
          return {
            "@name": "",
            "@item-type": "2",
            "@value": this.float.to(object as number),
            "@operator": this.integer.to(operator),
          };
          case ExpressionReferenceKinds.Function:
            return {
              "@name": "",
              "@item-type": "3",
              "@value": this.functionKind.to(object as number),
              "@operator": this.integer.to(operator),
            };
        default:
          return undefined;
      }
    },
  };
  private readonly functionKind = new MapTranslator<ExpressionFunctionKinds, string>([
    { object: ExpressionFunctionKinds.Root,             bean: "2" },
    { object: ExpressionFunctionKinds.NaturalLogarithm, bean: "LN" },
  ], undefined, undefined);
  private readonly expressionObjectReference = {
    from: (bean: ICalculateItemBean): ICalculationExpressionObjectReference => {
      const result = this.objectReference.from(bean) as ICalculationExpressionObjectReference;
      result.expressionKind = (result.type === "calculate") ? this.expressionKind.from(bean["@option"]) : undefined;
      result.alternativeValue = this.float.from(bean["@empty-value"]);
      return result;
    },
    to: (object: ICalculationExpressionObjectReference, operator: ExpressionOperatorKinds): ICalculateItemBean => {
      const result = this.objectReference.to(object, operator);
      result["@option"] = (object.type === "calculate") ? this.expressionKind.to(object.expressionKind) : undefined;
      result["@empty-value"] = this.float.to(object.alternativeValue);
      return result;
    },
  };
  private readonly expressionKind = new MapTranslator<ExpressionKinds, string>([
    { object: ExpressionKinds.Calculation,  bean: "1" },
    { object: ExpressionKinds.Conditional,  bean: "2" },
    { object: ExpressionKinds.DateDuration, bean: "3" },
    { object: ExpressionKinds.TimeSpan,     bean: "5" },
  ], undefined, undefined);
  private readonly integer = new IntegerTranslator();
  private readonly float = new FloatTranslator();
  private readonly items = new ItemsTranslator();
  private readonly objectReference = new ExpressionObjectReferenceTranslator();  
  private readonly brackets = new ExpressionBracketsTranslator();
  private readonly conditionalExpressionBehavior = new CalculationConditionalExpressionBehaviorTranslator();
}

export class ConditionalExpressionBehaviorTranslator implements IExpressinBehaviorTranlatable {
  from(bean: ICalculateObjectBean): IConditionalExpressionBehavior {
    return {
      contents: this.items.from(
        bean.calculateitem,
        s => {
          const logicalOperator = this.logicalOperator.from(s["@condition"]);
          return {
            reference: this.expressionObjectReference.from(s),
            operator: this.integer.from(s["@operator"]),
            value: s["@value"],
            brackets: this.brackets.from(s),
            logicalOperator,
            presentation: this.presentation.from(s),
          };
        },
      ),
    };
  }

  to(object: IConditionalExpressionBehavior): { calculateitem: ICalculateItemBean | ICalculateItemBean[], relation: IXElementContentsObjectRelationalBean } {
    return {
      calculateitem: this.items.to(
        object.contents,
        s => {
          const result = this.expressionObjectReference.to(s.reference, s.operator);
          result["@obj-id"] = undefined;                                      // 条件式は設定しない.v1実装仕様に合わせる.
          // @condition設定によらず@view*-*は出力する.(条件を未指定にしたら戻す必要があるため.)
          const presentation = this.presentation.to(s.presentation);
          result["@view-type"] = presentation.viewType;
          result["@view-text"] = presentation.viewText;
          result["@viewobj-type"] = presentation.viewobjType;
          result["@viewobj-key"] = presentation.viewobjKey;
          result["@viewobj-subkey"] = presentation.viewobjSubkey;
          result["@viewobj-option"] = presentation.viewobjOption;
          result["@value"] = s.value;
          result["@operator"] = this.integer.to(s.operator);
          const brackets = this.brackets.to(s.brackets);
          result["@priority-front"] = brackets.priorityFront;
          result["@priority-back"] = brackets.priorityBack;
          result["@condition"] = this.logicalOperator.to(s.logicalOperator);
          return result;
        },
      ),
      relation: undefined,
    };
  }

  private readonly logicalOperator = new MapTranslator<ExpressionLogicalOperatorKinds, string>([
    { object: ExpressionLogicalOperatorKinds.And, bean: "1" },
    { object: ExpressionLogicalOperatorKinds.Or,  bean: "2" },
  ], ExpressionLogicalOperatorKinds.None, undefined);
  private readonly presentation = {
    from: (bean: ICalculateItemBean): IConditionalExpressionPresentation => {
      const kind = this.presentaionKind.from(bean["@view-type"]);
      return {
        kind,
        reference: (kind === ExpressionReferenceKinds.Object)
          ? this.objectPresentation.from(bean)
          : bean["@view-text"],
      };
    },
    to: (object: IConditionalExpressionPresentation) => {
      const result: any = (object.kind === ExpressionReferenceKinds.Object)
        ? this.objectPresentation.to(object.reference as ILinkedObjectSpecifiable)
        : {
          viewText: object.reference as string,
        };
      result.viewType = this.presentaionKind.to(object.kind);
      return result;
    },
  };
  private readonly presentaionKind = new MapTranslator<ExpressionReferenceKinds, string>([
    { object: ExpressionReferenceKinds.Object, bean: "3" },
    // 2:日付は実際には使われてない模様.
    { object: ExpressionReferenceKinds.Fixed,  bean: "1" },
  ], undefined, undefined);
  private readonly integer = new IntegerTranslator();
  private readonly items = new ItemsTranslator();
  private readonly objectReference = new ExpressionObjectReferenceTranslator();
  private readonly expressionObjectReference = {
    from: (bean: ICalculateItemBean): IConditionalExpressionObjectReference => {
      const result = this.objectReference.from(bean) as IConditionalExpressionObjectReference;
      if (result.type === "calculate") {
        result.expressionKind = this.expressionKind.from(bean["@option"]);
        result.dateDurationKind = this.dateDurationKind.from(bean["@option"]);
      }
      return result;
    },
    to: (object: IConditionalExpressionObjectReference, operator: ExpressionOperatorKinds): ICalculateItemBean => {
      const result = this.objectReference.to(object, operator);
      if (object.type === "calculate") {
        result["@option"] = this.expressionKind.to(object.expressionKind, object.dateDurationKind);
      }
      return result;
    },
  };
  private readonly expressionKind = {
    from: (bean: string): ExpressionKinds => {
      switch (bean) {
        case "1":
          return ExpressionKinds.Calculation;
        case "3":
        case "6":
        case "7":
          return ExpressionKinds.DateDuration;
        default:
          return undefined;
      }
    },
    to: (expressionKind: ExpressionKinds, dateDurationKind: DateDurationKinds): string => {
      switch (expressionKind) {
        case ExpressionKinds.Calculation:
          return "1";
        case ExpressionKinds.DateDuration:
          return this.dateDurationKind.to(dateDurationKind);
        default:
          return undefined;
      }
    },
  };
  private readonly dateDurationKind = new MapTranslator<DateDurationKinds, string>([
    { object: DateDurationKinds.Days,  bean: "3" },
    { object: DateDurationKinds.Weeks, bean: "4" },
    { object: DateDurationKinds.Years, bean: "6" },
    { object: DateDurationKinds.Age,   bean: "7" },
  ], undefined, undefined);
  private readonly objectPresentation = new ExpressionObjectPresentationTranslator();
  private readonly brackets = new ExpressionBracketsTranslator();
}

export class DateDurationExpressionBehaviorTranslator implements IExpressinBehaviorTranlatable {
  from(bean: ICalculateObjectBean): IDateDurationExpressionBehavior {
    const items = this.arrayComplex.from(bean.calculateitem);
    return (items.length < 2)
      ? {
        from: null,
        to: null,
        kind: DateDurationKinds.Days,
        isContainedFrom: false,
      }
      : {
        from: this.content.from(items[1]),
        to: this.content.from(items[0]),
        kind: this.integer.from(items[0]["@option"]), // 全itemに同じ値が設定されてるので1つ目を使う.
        isContainedFrom: (2 < items.length),          // 3つ目に固定値のitemがあるはずなので.
    };
  }

  to(object: IDateDurationExpressionBehavior): { calculateitem: ICalculateItemBean | ICalculateItemBean[], relation: IXElementContentsObjectRelationalBean } {
    if (!object.from || !object.to) {
      return {
        calculateitem: undefined,
        relation: undefined,
      };
    }
    const results = [
      this.content.to(object.to, object.kind, ExpressionOperatorKinds.Subtract),
      this.content.to(object.from, object.kind, ExpressionOperatorKinds.Add),     // 3つ目がありえるので加算にしている.
    ];
    if (object.isContainedFrom) {
      results.push({
        "@name": "",
        "@item-type": "2",
        "@option": this.integer.to(object.kind),
        "@value": "1",
        "@operator": this.integer.to(ExpressionOperatorKinds.Add),                // 4つ目はないので何でもいいはずだがv1に合わせる.
      });
    }
    return {
      calculateitem: results,
      relation: undefined,
    };
  }

  private readonly content = {
    from: (bean: ICalculateItemBean) => this.objectReference.from(bean),
    to: (object: ILinkedObjectSpecifiable, kind: DateDurationKinds, operator: ExpressionOperatorKinds) => {
      const result = this.objectReference.to(object, operator);
      result["@option"] = this.integer.to(kind);
      return result;
    },
  };
  private readonly integer = new IntegerTranslator();
  private readonly arrayComplex = new ArrayComplexTranslator();
  private readonly objectReference = new ExpressionObjectReferenceTranslator();
}

export class TimeSpanExpressionBehaviorTranslator implements IExpressinBehaviorTranlatable {
  from(bean: ICalculateObjectBean): ITimeSpanExpressionBehavior {
    const items = this.arrayComplex.from(bean.calculateitem);
    return (items.length < 2)
      ? {
        from: null,
        to: null,
        kind: TimeSpanKinds.DayHourMinute,
      }
      : {
        from: this.content.from(items[1]),
        to: this.content.from(items[0]),
        kind: this.integer.from(items[0]["@option"]), // 全itemに同じ値が設定されてるので1つ目を使う.
    };
  }

  to(object: ITimeSpanExpressionBehavior): { calculateitem: ICalculateItemBean | ICalculateItemBean[], relation: IXElementContentsObjectRelationalBean } {
    if (!object.from || !object.to) {
      return {
        calculateitem: undefined,
        relation: undefined,
      };
    }
    return {
      calculateitem: [
        this.content.to(object.to, object.kind, ExpressionOperatorKinds.Subtract),
        this.content.to(object.from, object.kind, ExpressionOperatorKinds.Subtract),  // 3つ目はないので何でもいいはずだがv1に合わせる.
      ],
      relation: undefined,
    };
  }

  private readonly content = {
    from: (bean: ICalculateItemBean) => this.objectReference.from(bean),
    to: (object: ILinkedObjectSpecifiable, kind: TimeSpanKinds, operator: ExpressionOperatorKinds) => {
      const result = this.objectReference.to(object, operator);
      result["@option"] = this.integer.to(kind);
      return result;
    },
  };
  private readonly integer = new IntegerTranslator();
  private readonly arrayComplex = new ArrayComplexTranslator();
  private readonly objectReference = new ExpressionObjectReferenceTranslator();
}

export class DateComparisonExpressionBehaviorTranslator implements IExpressinBehaviorTranlatable {
  from(bean: ICalculateObjectBean): IDateComparisonExpressionBehavior {
    return {
      contents: this.items.from(
        bean.calculateitem,
        s => ({
          reference: this.objectReference.from(s),
          operator: this.integer.from(s["@operator"]),
        })),
    };
  }
  to(object: IDateComparisonExpressionBehavior): { calculateitem: ICalculateItemBean | ICalculateItemBean[], relation: IXElementContentsObjectRelationalBean } {
    return {
      calculateitem: this.items.to(
        object.contents,
        s => this.objectReference.to(s.reference, s.operator),
      ),
      relation: undefined,
    };
  }

  private readonly integer = new IntegerTranslator();
  private readonly items = new ItemsTranslator();
  private readonly objectReference = new ExpressionObjectReferenceTranslator();
}

export class UndefinedExpressionBehaviorTranslator implements IExpressinBehaviorTranlatable {
  from(bean: ICalculateObjectBean): IUndefinedExpressionBehavior {
    // 未知の値が入ってきた場合は前値保持するのでbeanを記録しておく.
    return {
      source: bean,
    };
  }

  to(object: IUndefinedExpressionBehavior): { calculateitem: ICalculateItemBean | ICalculateItemBean[], relation: IXElementContentsObjectRelationalBean } {
    // 未知の値だった場合はbeanを書き戻す.
    return object.source;
  }
}

class ExpressionObjectReferenceTranslator {
  from(bean: ICalculateItemBean): ILinkedObjectSpecifiable {
    return {
      type: bean["@type"],
      id: this.integer.from(bean["@obj-id"]),
      key: bean["@key"],
      subKey: bean["@subkey"],
    };
  }

  to(object: ILinkedObjectSpecifiable, operator: ExpressionOperatorKinds): ICalculateItemBean {
    return {
      "@name": "",
      "@item-type": "1",
      "@type": object.type,
      "@key": object.key,
      "@subkey": object.subKey,
      "@obj-id": this.integer.to(object.id),
      "@operator": this.integer.to(operator),
    };
  }

  private readonly integer = new IntegerTranslator();
}

class ExpressionObjectPresentationTranslator {
  from(bean: ICalculateItemBean): IConditionalExpressionObjectReference {
    const isCalculate = (bean["@view-type"] === "calculate");
    return {
      type: bean["@viewobj-type"],
      id: this.integer.from(bean["@viewobj-id"]),
      key: bean["@viewobj-key"],
      subKey: bean["@viewobj-subkey"],
      expressionKind: isCalculate ? this.expressionKind.from(bean["@viewobj-option"]) : undefined,
      dateDurationKind: isCalculate ? this.dateDurationKind.from(bean["@viewobj-option"]) : undefined,
    };
  }

  to(object: IConditionalExpressionObjectReference) {
    return {
      viewType: "1",
      viewobjType: object.type,
      viewobjKey: object.key,
      viewobjSubkey: object.subKey,
      viewobjId: this.integer.to(object.id),
      viewobjOption: (object.type === "calculate") ? this.expressionKind.to(object.expressionKind, object.dateDurationKind) : undefined,
    };
  }

  private readonly integer = new IntegerTranslator();
  private readonly expressionKind = {
    from: (bean: string): ExpressionKinds => {
      switch (bean) {
        case "1":
          return ExpressionKinds.Calculation;
        case "3":
        case "6":
        case "7":
          return ExpressionKinds.DateDuration;
        default:
          return undefined;
      }
    },
    to: (expressionKind: ExpressionKinds, dateDurationKind: DateDurationKinds): string => {
      switch (expressionKind) {
        case ExpressionKinds.Calculation:
          return "1";
        case ExpressionKinds.DateDuration:
          return this.dateDurationKind.to(dateDurationKind);
        default:
          return undefined;
      }
    },
  };
  private readonly dateDurationKind = new MapTranslator<DateDurationKinds, string>([
    { object: DateDurationKinds.Days,  bean: "3" },
    { object: DateDurationKinds.Weeks, bean: "4" },
    { object: DateDurationKinds.Years, bean: "6" },
    { object: DateDurationKinds.Age,   bean: "7" },
  ], undefined, undefined);
}

class ExpressionBracketsTranslator {
  from(bean: ICalculateItemBean): IExpressionContentBrackets {
    return {
      openCount: this.openCount.from(bean["@priority-front"]),
      closeCount: this.closeCount.from(bean["@priority-back"]),
    };
  }

  to(object: IExpressionContentBrackets) {
    return {
      priorityFront: this.openCount.to(object.openCount),
      priorityBack: this.closeCount.to(object.closeCount),
    };
  }

  private readonly openCount = new MapTranslator<number, string>([
    { object: 1,  bean: "("   },
    { object: 2,  bean: "(("  },
    { object: 3,  bean: "(((" },
  ], 0, undefined);
  private readonly closeCount = new MapTranslator<number, string>([
    { object: 1,  bean: ")"   },
    { object: 2,  bean: "))"  },
    { object: 3,  bean: ")))" },
  ], 0, undefined);
}
